// @givmo/app-charity — donor analytics (CPB-WEB6).
//
// A charity's own giving history, aggregated: lifetime totals, a year-by-year table, and retention by
// the year a donor first gave. Unlike the Contacts donor tab, analytics carries NO donor identity — it
// is counts and sums only — so it is NOT consent-gated and stays readable for every claimed charity.
//
// ★ THE NUMBERS ARE THE LEDGER'S. Every count and every money string on this screen is the server's
// own value, rendered verbatim. The backend computes the yearly gross/net sums from the SAME ledger
// enumeration the §321 statement uses, so these figures tie to the compliance report to the penny by
// construction. This screen therefore performs NO arithmetic whatsoever:
//
//   • no client re-summing of the yearly rows into a lifetime total (the server sends its own);
//   • no percentages, ratios, or deltas — none exist on the wire and none are computed here;
//   • the up/down markers come from COMPARING adjacent years' values (see `compareMoneyStrings`),
//     which yields an ordering, never a number. Nothing derived is ever displayed as a value.
//
// A table, not a chart: this ships with no charting dependency, deliberately. Exact rows a development
// director can read and reconcile beat a graph she cannot tie to anything.
//
// VOCABULARY: retention groups are keyed on FIRST-GIFT YEAR. The backend's wire vocabulary is
// `first_donation_year` and the quarantined word for this concept must never appear in this feature.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  charityAdminGetCrmAnalyticsSummaryOptions,
  charityAdminGetCrmRetentionOptions,
} from '@givmo/api-client/query';
import {
  sdk,
  type CharityAnalyticsSummaryOut,
  type CharityAnalyticsYearOut,
  type CharityRetentionOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  StatCard,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  Building2,
  CalendarDays,
  ChartNoAxesColumn,
  ChevronDown,
  ChevronUp,
  Coins,
  Download,
  Minus,
  Repeat,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { useCharity } from '../grants/use-charity';
import { fallbackCsvName, runCsvDownload } from '../grants/csv-download';
import {
  countTrend,
  displayCount,
  displayDate,
  displayMoney,
  isSliceUnavailable,
  moneyTrend,
  serverMessage,
  type YearTrend,
} from '@givmo/crm';

const PREFIX = 'analytics';

export function AnalyticsPage() {
  const { membership, charityId, isPending, isError, error, refetch } = useCharity();
  const [downloading, setDownloading] = useState(false);

  const summaryQuery = useQuery({
    ...charityAdminGetCrmAnalyticsSummaryOptions({ path: { charity_id: charityId ?? '' } }),
    enabled: charityId !== null,
    retry: false,
  });

  const retentionQuery = useQuery({
    ...charityAdminGetCrmRetentionOptions({ path: { charity_id: charityId ?? '' } }),
    enabled: charityId !== null,
    retry: false,
  });

  const downloadCsv = async () => {
    if (charityId === null) return;
    setDownloading(true);
    try {
      await runCsvDownload(
        (options) =>
          sdk.charityAdminExportCrmAnalyticsCsv({ ...options, path: { charity_id: charityId } }),
        fallbackCsvName('analytics-yearly'),
      );
      toast.success('Yearly summary downloaded');
    } catch (downloadError) {
      toast.error(
        serverMessage(downloadError, 'Could not download the yearly summary. Please try again.'),
      );
    } finally {
      setDownloading(false);
    }
  };

  // ── Fail-closed org gates. ──────────────────────────────────────────────────────────────────────
  if (isPending) {
    return (
      <PageShell>
        <Card className="p-24" testId={`${PREFIX}-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (isError) {
    return (
      <PageShell>
        <EmptyState
          testId={`${PREFIX}-memberships-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your organization"
          description={serverMessage(
            error,
            'We couldn’t load your organization. Please try again.',
          )}
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={refetch}
              testId={`${PREFIX}-memberships-retry`}
            >
              Try again
            </Button>
          }
        />
      </PageShell>
    );
  }

  if (!membership || charityId === null) {
    return (
      <PageShell>
        <EmptyState
          testId={`${PREFIX}-no-org`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Claim your organization first"
          description="Once your organization is verified, its giving history appears here."
        />
      </PageShell>
    );
  }

  if (summaryQuery.isPending) {
    return (
      <PageShell orgName={membership.name}>
        <Card className="p-24" testId={`${PREFIX}-summary-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (summaryQuery.isError) {
    const unavailable = isSliceUnavailable(summaryQuery.error);
    return (
      <PageShell orgName={membership.name}>
        <EmptyState
          testId={unavailable ? `${PREFIX}-unavailable` : `${PREFIX}-error`}
          icon={<Icon icon={unavailable ? ChartNoAxesColumn : ShieldAlert} size={20} aria-hidden />}
          title={unavailable ? 'Not available yet' : 'Couldn’t load your analytics'}
          description={
            unavailable
              ? 'This part of the portal isn’t switched on for your organization yet.'
              : serverMessage(summaryQuery.error, 'We couldn’t load your analytics.')
          }
          action={
            unavailable ? undefined : (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void summaryQuery.refetch()}
                testId={`${PREFIX}-retry`}
              >
                Try again
              </Button>
            )
          }
        />
      </PageShell>
    );
  }

  const summary: CharityAnalyticsSummaryOut = summaryQuery.data;
  const years = summary.years ?? [];

  return (
    <PageShell orgName={membership.name}>
      <div className="flex flex-wrap items-center justify-between gap-12">
        <p className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
          Every figure is taken straight from your organization’s giving records.
        </p>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
          loading={downloading}
          disabled={downloading}
          onClick={() => void downloadCsv()}
          testId="analytics-csv"
        >
          Download CSV
        </Button>
      </div>

      {/* ── Lifetime tiles — server aggregates, verbatim. ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-16 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          testId={`${PREFIX}-tile-donations`}
          label="Donations"
          value={displayCount(summary.donation_count)}
          icon={<Icon icon={Coins} size={18} aria-hidden />}
          sublabel="All time"
        />
        <StatCard
          testId={`${PREFIX}-tile-donors`}
          label="Donors"
          value={displayCount(summary.distinct_donor_count)}
          icon={<Icon icon={Users} size={18} aria-hidden />}
          sublabel="All time"
        />
        <StatCard
          testId={`${PREFIX}-tile-recurring`}
          label="Recurring donors"
          value={displayCount(summary.recurring_donor_count)}
          icon={<Icon icon={Repeat} size={18} aria-hidden />}
          // The definition matters: a basket (ETF) plan is not a commitment to this organization and
          // is excluded by the backend. Say so where the number is read.
          sublabel={`${displayCount(summary.recurring_plan_count)} active plans to your organization`}
        />
        <StatCard
          testId={`${PREFIX}-tile-first`}
          label="First gift"
          value={displayDate(summary.first_donation_date)}
          icon={<Icon icon={CalendarDays} size={18} aria-hidden />}
        />
        <StatCard
          testId={`${PREFIX}-tile-last`}
          label="Last gift"
          value={displayDate(summary.last_donation_date)}
          icon={<Icon icon={CalendarDays} size={18} aria-hidden />}
        />
      </div>

      <YearlySection years={years} />

      <RetentionSection
        isPending={retentionQuery.isPending}
        isError={retentionQuery.isError}
        error={retentionQuery.error}
        retention={retentionQuery.data}
        onRetry={() => void retentionQuery.refetch()}
      />
    </PageShell>
  );
}

// ── Yearly table ──────────────────────────────────────────────────────────────────────────────────

function YearlySection({ years }: { years: readonly CharityAnalyticsYearOut[] }) {
  return (
    <Card testId={`${PREFIX}-yearly-card`}>
      <CardHeader>
        <div className="flex flex-col">
          <span className="font-w600 text-text-strong">Year by year</span>
          <span className="text-s12 text-text-tertiary">
            Gross is before fees; net is what reached your organization.
          </span>
        </div>
      </CardHeader>
      <CardBody>
        {/* The testid sits on the section wrapper so it is stable whether or not there are rows. */}
        <div className="overflow-x-auto" {...testIdProp('analytics-yearly-table')}>
          {years.length === 0 ? (
            <EmptyState
              testId={`${PREFIX}-yearly-empty`}
              icon={<Icon icon={ChartNoAxesColumn} size={20} aria-hidden />}
              title="No donations yet"
              description="When your first gift comes in through Givmo, its year appears here."
            />
          ) : (
            <Table className="min-w-[52rem]">
              <THead>
                <tr>
                  <TH>Year</TH>
                  <TH align="right">Donations</TH>
                  <TH align="right">Gross</TH>
                  <TH align="right">Net</TH>
                  <TH align="right">Donors</TH>
                  <TH align="right">New</TH>
                  <TH align="right">Returning</TH>
                </tr>
              </THead>
              <TBody>
                {years.map((year, index) => {
                  // Compare against the PREVIOUS year only. `years` arrives ascending from the server.
                  const previous = index > 0 ? years[index - 1] : undefined;
                  return (
                    <TR key={year.year} testId={`${PREFIX}-year-row-${year.year}`}>
                      <TD>
                        <span className="font-w600 text-text-strong tabular-nums">{year.year}</span>
                      </TD>
                      <TD align="right" numeric>
                        <TrendValue
                          value={displayCount(year.donation_count)}
                          trend={countTrend(year.donation_count, previous?.donation_count)}
                        />
                      </TD>
                      <TD align="right" numeric>
                        <TrendValue
                          value={displayMoney(year.gross_total)}
                          trend={moneyTrend(year.gross_total, previous?.gross_total)}
                          strong
                        />
                      </TD>
                      <TD align="right" numeric>
                        <TrendValue
                          value={displayMoney(year.net_total)}
                          trend={moneyTrend(year.net_total, previous?.net_total)}
                        />
                      </TD>
                      <TD align="right" numeric>
                        <TrendValue
                          value={displayCount(year.distinct_donor_count)}
                          trend={countTrend(
                            year.distinct_donor_count,
                            previous?.distinct_donor_count,
                          )}
                        />
                      </TD>
                      <TD align="right" numeric>
                        <span className="text-text-secondary">
                          {displayCount(year.new_donor_count)}
                        </span>
                      </TD>
                      <TD align="right" numeric>
                        <span className="text-text-secondary">
                          {displayCount(year.returning_donor_count)}
                        </span>
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

const TREND_ICON = { up: ChevronUp, down: ChevronDown, flat: Minus } as const;
const TREND_LABEL = {
  up: 'Higher than the year before',
  down: 'Lower than the year before',
  flat: 'Unchanged from the year before',
} as const;

/**
 * A server value with a direction marker against the previous year. The MARKER is the only thing the
 * client contributes, and it is a comparison — there is no delta, no percentage, no derived number.
 */
function TrendValue({
  value,
  trend,
  strong = false,
}: {
  value: string;
  trend: YearTrend;
  strong?: boolean;
}) {
  return (
    <span className="inline-flex items-center justify-end gap-4">
      <span className={strong ? 'font-w600 text-text-strong' : 'text-text-secondary'}>{value}</span>
      {trend === 'none' ? null : (
        <Icon
          icon={TREND_ICON[trend]}
          size={14}
          className={
            trend === 'up'
              ? 'text-success-600'
              : trend === 'down'
                ? 'text-text-tertiary'
                : 'text-icon-muted'
          }
          aria-label={TREND_LABEL[trend]}
        />
      )}
    </span>
  );
}

// ── Retention ─────────────────────────────────────────────────────────────────────────────────────

function RetentionSection({
  isPending,
  isError,
  error,
  retention,
  onRetry,
}: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  retention: CharityRetentionOut | undefined;
  onRetry: () => void;
}) {
  const groups = retention?.groups ?? [];
  // The calendar year the per-group series extends through; null on zero data.
  const latestYear = retention?.latest_data_year ?? null;

  return (
    <Card testId={`${PREFIX}-retention-card`}>
      <CardHeader>
        <div className="flex flex-col">
          <span className="font-w600 text-text-strong">Donors who came back</span>
          <span className="text-s12 text-text-tertiary">
            Grouped by the year each donor first gave, then how many of that group gave again in
            each later year.
          </span>
        </div>
      </CardHeader>
      <CardBody>
        <div className="overflow-x-auto" {...testIdProp('analytics-retention')}>
          {isPending ? (
            <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-retention-loading`)}>
              <Skeleton className="h-12 w-1/3" />
              <Skeleton className="h-12 w-2/3" />
            </div>
          ) : isError && isSliceUnavailable(error) ? (
            <EmptyState
              testId={`${PREFIX}-retention-unavailable`}
              icon={<Icon icon={ChartNoAxesColumn} size={20} aria-hidden />}
              title="Not available yet"
              description="This part of the portal isn’t switched on for your organization yet."
            />
          ) : isError ? (
            <EmptyState
              testId={`${PREFIX}-retention-error`}
              icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
              title="Couldn’t load returning donors"
              description={serverMessage(error, 'We couldn’t load this section.')}
              action={
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={onRetry}
                  testId={`${PREFIX}-retention-retry`}
                >
                  Try again
                </Button>
              }
            />
          ) : groups.length === 0 ? (
            <EmptyState
              testId={`${PREFIX}-retention-empty`}
              icon={<Icon icon={Users} size={20} aria-hidden />}
              title="Not enough history yet"
              description="Once donors have given across more than one year, their return rate shows here."
            />
          ) : (
            <div className="flex flex-col gap-12">
              {groups.map((group) => (
                <div
                  key={group.first_donation_year}
                  className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface p-16"
                  {...testIdProp(`${PREFIX}-retention-group-${group.first_donation_year}`)}
                >
                  <div className="flex flex-wrap items-center gap-8">
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong tabular-nums">
                      First gave in {group.first_donation_year}
                    </span>
                    <Badge tone="neutral" size="sm">
                      {displayCount(group.donor_count)} donors
                    </Badge>
                  </div>
                  {group.subsequent_years.length === 0 ? (
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      No later years to show yet.
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-8">
                      {group.subsequent_years.map((year) => (
                        <span
                          key={year.year}
                          className="flex flex-col rounded-md border border-border-subtle px-12 py-6"
                        >
                          <span className="font-sans text-s11 font-w500 tracking-givmo text-text-tertiary tabular-nums">
                            {year.year}
                          </span>
                          <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong tabular-nums">
                            {displayCount(year.donor_count)}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {latestYear !== null ? (
                <p
                  className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                  {...testIdProp(`${PREFIX}-retention-through`)}
                >
                  Counts run through {latestYear}. The current year is counted so far — never
                  projected.
                </p>
              ) : null}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// ── Shell ─────────────────────────────────────────────────────────────────────────────────────────

function PageShell({ children, orgName }: { children: React.ReactNode; orgName?: string }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[1120px] flex-col gap-16 px-24 py-24"
      {...testIdProp('page-analytics')}
    >
      <PageHeader
        title="Analytics"
        subtitle="Your organization’s giving history — counts and totals only, no donor details."
        organizationName={orgName}
        organizationNameTestId={`${PREFIX}-org-badge`}
      />
      {children}
    </div>
  );
}
